import React from "react";
import { useLanguage } from "@/context/LanguageContext";
import { motion } from "framer-motion";
import { ArrowDown, Clock, Shield } from "lucide-react";

export default function HeroSection() {
  const { t, lang } = useLanguage();

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section data-testid="hero-section" className="relative min-h-[100vh] flex items-center overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <img
          src="https://images.unsplash.com/photo-1591365063270-4a3e0c6b5d58"
          alt="Houston skyline at night"
          className="w-full h-full object-cover scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B0A08]/75 via-[#0B0A08]/50 to-[#0B0A08]" />
      </div>

      {/* Decorative elements */}
      <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-[#D4AF37]/5 rounded-full blur-[120px]" />
      <div className="absolute bottom-1/4 left-1/4 w-64 h-64 bg-[#D4AF37]/3 rounded-full blur-[100px]" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-16 w-full py-32">
        <div className="max-w-3xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="flex items-center gap-3 mb-8"
          >
            <span className="inline-flex items-center gap-2 bg-[#D4AF37]/10 border border-[#D4AF37]/20 text-[#D4AF37] px-4 py-2 rounded-full text-xs font-bold tracking-[0.2em] uppercase">
              <span className="w-2 h-2 bg-[#D4AF37] rounded-full animate-pulse" />
              {t.hero.badge}
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="font-heading text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tighter text-white leading-[1.05] mb-8"
          >
            {t.hero.titleLine1}
            <br />
            <span className="text-gold-gradient">{t.hero.titleHighlight}</span>{" "}
            {t.hero.titleLine2}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="text-base lg:text-lg text-[#D1D0CC] leading-relaxed mb-12 max-w-xl"
          >
            {t.hero.subtitle}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.45 }}
            className="flex flex-col sm:flex-row gap-4 mb-16"
          >
            <button
              data-testid="hero-cta-emergency"
              onClick={() => scrollTo("contact")}
              className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#BF953F] via-[#F9E58A] to-[#B38728] text-black px-8 py-4 text-sm font-bold rounded-full hover:shadow-[0_0_30px_rgba(212,175,55,0.4)] transition-all duration-300 transform hover:-translate-y-1"
            >
              {t.hero.cta}
            </button>
            <button
              data-testid="hero-cta-phone"
              onClick={() => scrollTo("contact")}
              className="inline-flex items-center justify-center gap-2 border border-[#D4AF37]/40 text-[#D4AF37] px-8 py-4 text-sm font-semibold rounded-full hover:bg-[#D4AF37]/10 transition-all duration-300"
            >
              <Clock className="w-4 h-4" />
              {t.hero.ctaSecondary}
            </button>
          </motion.div>

          {/* Trust badges */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
            className="flex flex-wrap gap-6"
          >
            {[
              { icon: Shield, text: lang === "es" ? "Calidad y seguridad" : "Quality & Security" },
              { icon: Clock, text: lang === "es" ? "Servicio 24/7" : "24/7 Service" },
            ].map((badge, i) => (
              <div key={i} className="flex items-center gap-2 text-[#8A8881] text-sm">
                <badge.icon className="w-4 h-4 text-[#D4AF37]/60" />
                <span>{badge.text}</span>
              </div>
            ))}
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
      >
        <button data-testid="scroll-down-btn" onClick={() => scrollTo("services")} className="text-white/30 hover:text-[#D4AF37] transition-colors">
          <ArrowDown className="w-5 h-5 animate-bounce" />
        </button>
      </motion.div>
    </section>
  );
}
